import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { contactService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import AuroraLoadingOverlay from '../components/AuroraLoadingOverlay';

const REL_LABEL: Record<string, string> = {
  romantico: '❤️ Romântico',
  amizade: '🤝 Amizade',
  familiar: '👨‍👩‍👧 Familiar',
  profissional: '💼 Profissional',
};

const QUESTION_HINT =
  'Quanto mais detalhes você incluir — situação atual, sentimentos envolvidos, o que já aconteceu e o que deseja entender — mais precisa e personalizada será a resposta de Aurora.';

function renderText(text: string): React.ReactNode {
  return text.split('\n').map((line, i) => {
    const parts: React.ReactNode[] = [];
    let last = 0;
    const boldRegex = /\*\*(.+?)\*\*/g;
    let match: RegExpExecArray | null;
    while ((match = boldRegex.exec(line)) !== null) {
      if (match.index > last) parts.push(line.slice(last, match.index));
      parts.push(<strong key={match.index} className="text-white font-semibold">{match[1]}</strong>);
      last = match.index + match[0].length;
    }
    if (last < line.length) parts.push(line.slice(last));
    if (line.trim() === '') return <div key={i} className="h-2" />;
    const isTitle = line.trim().startsWith('**') && parts.some(p => (p as any)?.type === 'strong');
    if (isTitle) return <h3 key={i} className="text-base font-semibold text-purple-300 mt-5 mb-2">{parts}</h3>;
    return <p key={i} className="text-white/80 text-sm leading-relaxed text-justify">{parts}</p>;
  });
}

const TOKEN_COST = 1;

const SinastriaPage: React.FC = () => {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  const [contact, setContact] = useState<any>(null);
  const [loadingContact, setLoadingContact] = useState(true);

  const [analysis, setAnalysis] = useState('');
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [errorAnalysis, setErrorAnalysis] = useState<string | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(true);

  const [conselho, setConselho] = useState('');
  const [loadingConselho, setLoadingConselho] = useState(false);
  const [errorConselho, setErrorConselho] = useState<string | null>(null);

  const [showPerguntaForm, setShowPerguntaForm] = useState(false);
  const [perguntaInput, setPerguntaInput] = useState('');
  const [perguntaQA, setPerguntaQA] = useState<{ question: string; answer: string }[]>([]);
  const [loadingPergunta, setLoadingPergunta] = useState(false);
  const [errorPergunta, setErrorPergunta] = useState<string | null>(null);

  useEffect(() => {
    contactService.list().then(list => {
      const found = list.find((c: any) => String(c.id) === contactId);
      setContact(found || null);
    }).catch(() => setContact(null)).finally(() => setLoadingContact(false));
  }, [contactId]);

  // Carrega a sinastria já gerada anteriormente para esta conexão (se houver),
  // com conselho e perguntas já respondidas, sem gastar token.
  useEffect(() => {
    if (!contactId) return;
    contactService.getLatestSinastria(Number(contactId))
      .then(existing => {
        if (existing) {
          setAnalysis(existing.analysis);
          setConselho(existing.advice || '');
          setPerguntaQA(existing.follow_up_qa || []);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingExisting(false));
  }, [contactId]);

  const handleAnalysis = async () => {
    if (!contactId) return;
    setLoadingAnalysis(true);
    setErrorAnalysis(null);
    try {
      const res = await contactService.sinastria(Number(contactId));
      setAnalysis(res.analysis);
      refresh().catch(() => {});
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setErrorAnalysis(detail || 'Erro ao gerar sinastria.');
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const handleConselho = async () => {
    if (!contactId) return;
    setLoadingConselho(true);
    setErrorConselho(null);
    try {
      const res = await contactService.conselho(Number(contactId));
      setConselho(res.advice);
      refresh().catch(() => {});
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setErrorConselho(detail || 'Erro ao gerar conselho.');
    } finally {
      setLoadingConselho(false);
    }
  };

  const handlePergunta = async () => {
    if (!contactId || perguntaInput.trim().length < 10) return;
    setLoadingPergunta(true);
    setErrorPergunta(null);
    try {
      const res = await contactService.pergunta(Number(contactId), perguntaInput.trim());
      setPerguntaQA(prev => [...prev, { question: res.question, answer: res.answer }]);
      setShowPerguntaForm(false);
      setPerguntaInput('');
      refresh().catch(() => {});
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setErrorPergunta(detail || 'Erro ao responder pergunta.');
    } finally {
      setLoadingPergunta(false);
    }
  };

  const tokenBalance = (user as any)?.tokens_balance ?? 0;
  const noTokens = !((user as any)?.is_free) && !((user as any)?.is_admin) && tokenBalance < TOKEN_COST;

  const cardStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '1rem',
    padding: '2rem',
  };

  const btnPrimary = (disabled: boolean): React.CSSProperties => ({
    background: disabled ? 'rgba(139,92,246,0.3)' : 'linear-gradient(135deg, #7c3aed, #c026d3)',
    boxShadow: disabled ? 'none' : '0 0 20px rgba(139,92,246,0.35)',
  });

  return (
    <div className="min-h-screen bg-gradient-mystical theme-transition">
      <AuroraLoadingOverlay isLoading={loadingAnalysis || loadingConselho || loadingPergunta} message="Aurora está analisando a sinastria..." />
      <div className="max-w-2xl mx-auto px-4 py-10">

        {/* Cabeçalho */}
        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={() => navigate('/profile?tab=conexoes')}
            className="text-white/50 hover:text-white transition-colors text-sm"
          >
            ← Conexões
          </button>
          <h1 className="text-2xl font-bold text-white">Sinastria</h1>
        </div>

        {loadingContact ? (
          <p className="text-white/40 text-sm">Carregando...</p>
        ) : !contact ? (
          <p className="text-red-400 text-sm">Conexão não encontrada.</p>
        ) : (
          <>
            {/* Card da conexão */}
            <div style={cardStyle} className="mb-6">
              <div className="flex items-center gap-4">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #c026d3)' }}
                >
                  {contact.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xl font-semibold text-white">{contact.name}</span>
                    {contact.relationship_type && (
                      <span className="text-xs text-purple-300 bg-purple-500/15 px-2 py-0.5 rounded-full">
                        {REL_LABEL[contact.relationship_type] || contact.relationship_type}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-white/50">
                    {contact.birth_date?.slice(0, 10)}
                    {contact.birth_time ? ` às ${contact.birth_time.slice(0, 5)}` : ' (hora não informada)'}
                    {contact.birth_city ? ` · ${contact.birth_city}` : ''}
                  </p>
                </div>
                {/* Saldo de tokens */}
                <div className="text-right flex-shrink-0">
                  <div className="text-xs text-white/40">Saldo</div>
                  <div className={`text-sm font-bold ${noTokens ? 'text-red-400' : 'text-purple-300'}`}>
                    {tokenBalance} token{tokenBalance !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            </div>

            {/* Aviso sem tokens */}
            {noTokens && (
              <div className="mb-6 px-4 py-3 rounded-lg text-sm bg-amber-500/15 text-amber-300 border border-amber-500/30">
                Você precisa de pelo menos {TOKEN_COST} token para gerar a sinastria.{' '}
                <button onClick={() => navigate('/checkout')} className="underline font-semibold hover:text-amber-200">
                  Adquirir tokens →
                </button>
              </div>
            )}

            {/* Botão gerar sinastria */}
            {loadingExisting ? (
              <div style={cardStyle} className="mb-6 text-center">
                <p className="text-white/40 text-sm">Verificando sinastria já gerada...</p>
              </div>
            ) : !analysis && (
              <div style={cardStyle} className="mb-6 text-center">
                <div className="text-3xl mb-3">🔮</div>
                <h2 className="text-white font-semibold text-lg mb-2">Análise de Sinastria</h2>
                <p className="text-white/45 text-sm mb-6 leading-relaxed">
                  Aurora Lyz vai cruzar os mapas astrais e revelar a dinâmica desta relação.<br/>
                  <span className="text-purple-400">Custo: {TOKEN_COST} token</span>
                </p>
                {errorAnalysis && (
                  <div className="mb-4 px-4 py-3 rounded-lg text-sm bg-red-500/20 text-red-300 border border-red-500/30">
                    {errorAnalysis}
                  </div>
                )}
                <button
                  onClick={handleAnalysis}
                  disabled={loadingAnalysis || noTokens}
                  className="px-8 py-4 rounded-xl font-semibold text-white text-base transition-all disabled:opacity-40"
                  style={btnPrimary(loadingAnalysis || noTokens)}
                >
                  {loadingAnalysis ? '✨ Analisando os astros...' : '🔮 Gerar Sinastria'}
                </button>
              </div>
            )}

            {/* Resultado da sinastria */}
            {analysis && (
              <div style={cardStyle} className="mb-4">
                <div className="flex items-start justify-between gap-3 mb-6">
                  <h2 className="text-lg font-semibold text-white">✨ Análise de Aurora Lyz</h2>
                  <button
                    onClick={() => { setAnalysis(''); setConselho(''); setPerguntaQA([]); handleAnalysis(); }}
                    disabled={loadingAnalysis || noTokens}
                    className="text-xs text-white/40 hover:text-white/70 transition-colors flex-shrink-0 disabled:opacity-30"
                    title="Gera uma nova análise, substituindo esta (consome 1 token)"
                  >
                    🔄 Gerar nova
                  </button>
                </div>
                <div className="space-y-1">{renderText(analysis)}</div>
              </div>
            )}

            {/* Botões de ação pós-sinastria */}
            {analysis && (
              <div className="grid grid-cols-2 gap-3 mb-4">
                {/* Conselho */}
                {!conselho ? (
                  <button
                    onClick={handleConselho}
                    disabled={loadingConselho || noTokens}
                    className="py-3 px-4 rounded-xl font-semibold text-white text-sm transition-all disabled:opacity-40 flex flex-col items-center gap-1"
                    style={{ background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.3)' }}
                  >
                    <span className="text-xl">{loadingConselho ? '⏳' : '💡'}</span>
                    <span>{loadingConselho ? 'Gerando...' : 'Pedir Conselho'}</span>
                    <span className="text-xs text-white/40">{TOKEN_COST} token</span>
                  </button>
                ) : (
                  <div className="col-span-2 text-xs text-amber-300/60 text-center py-1">
                    Conselho gerado ↓
                  </div>
                )}

                {/* Pergunta */}
                {perguntaQA.length === 0 && !showPerguntaForm && (
                  <button
                    onClick={() => setShowPerguntaForm(true)}
                    disabled={noTokens}
                    className="py-3 px-4 rounded-xl font-semibold text-white text-sm transition-all disabled:opacity-40 flex flex-col items-center gap-1"
                    style={{ background: 'rgba(34,211,238,0.10)', border: '1px solid rgba(34,211,238,0.25)' }}
                  >
                    <span className="text-xl">❓</span>
                    <span>Fazer Pergunta</span>
                    <span className="text-xs text-white/40">{TOKEN_COST} token</span>
                  </button>
                )}
              </div>
            )}

            {/* Resultado do conselho */}
            {conselho && (
              <div style={{ ...cardStyle, borderColor: 'rgba(251,191,36,0.2)' }} className="mb-4">
                <h3 className="text-base font-semibold text-amber-300 mb-4">💡 Conselhos de Aurora Lyz</h3>
                {errorConselho && (
                  <div className="mb-3 px-3 py-2 rounded-lg text-xs bg-red-500/20 text-red-300 border border-red-500/30">{errorConselho}</div>
                )}
                <div className="space-y-1">{renderText(conselho)}</div>
              </div>
            )}

            {/* Formulário de pergunta */}
            {showPerguntaForm && (
              <div style={{ ...cardStyle, borderColor: 'rgba(34,211,238,0.2)' }} className="mb-4">
                <h3 className="text-base font-semibold text-cyan-300 mb-2">❓ Fazer uma Pergunta</h3>
                <p className="text-xs text-white/40 mb-4 leading-relaxed">{QUESTION_HINT}</p>
                <textarea
                  value={perguntaInput}
                  onChange={e => setPerguntaInput(e.target.value)}
                  rows={4}
                  placeholder="Ex: Existe futuro nessa relação? Sinto que temos muitos conflitos por ciúme — isso é algo que pode melhorar? Como podemos melhorar a comunicação?"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/25 focus:outline-none focus:border-cyan-400 text-sm resize-none"
                />
                <div className="text-right text-xs text-white/30 mt-1 mb-4">{perguntaInput.length} caracteres</div>
                {errorPergunta && (
                  <div className="mb-3 px-3 py-2 rounded-lg text-xs bg-red-500/20 text-red-300 border border-red-500/30">{errorPergunta}</div>
                )}
                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowPerguntaForm(false); setPerguntaInput(''); }}
                    className="flex-1 py-2.5 rounded-xl text-sm text-white/50 hover:text-white transition-colors border border-white/10"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handlePergunta}
                    disabled={loadingPergunta || perguntaInput.trim().length < 10}
                    className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40"
                    style={btnPrimary(loadingPergunta || perguntaInput.trim().length < 10)}
                  >
                    {loadingPergunta ? 'Consultando...' : `Enviar — ${TOKEN_COST} token`}
                  </button>
                </div>
              </div>
            )}

            {/* Resultado das perguntas (todas as perguntas feitas sobre esta sinastria) */}
            {perguntaQA.length > 0 && (
              <div className="space-y-3 mb-4">
                {perguntaQA.map((qa, i) => (
                  <div key={i} style={{ ...cardStyle, borderColor: 'rgba(34,211,238,0.2)' }}>
                    <h3 className="text-base font-semibold text-cyan-300 mb-2">❓ Sua Pergunta</h3>
                    <p className="text-sm text-white/60 italic mb-4">"{qa.question}"</p>
                    <div className="space-y-1">{renderText(qa.answer)}</div>
                  </div>
                ))}
                <button
                  onClick={() => setShowPerguntaForm(true)}
                  disabled={noTokens || showPerguntaForm}
                  className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors disabled:opacity-40"
                >
                  + Fazer outra pergunta ({TOKEN_COST} token)
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SinastriaPage;
